import { useState, useRef } from 'react';
import {
    Mic, Play, Pause, MapPin, FileText, Image, Video, Sticker, AlertCircle,
    Share2, BookOpen, ExternalLink, MousePointerClick,
} from 'lucide-react';
import type { GenericMessage } from '../types';
import styles from './GenericMessageBubble.module.css';

interface Props {
    message: GenericMessage;
}

function formatTimestamp(ts: string): string {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

function AudioPlayer({ src }: { src: string }) {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [error, setError] = useState(false);
    const [ready, setReady] = useState(false);

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (playing) {
            audio.pause();
        } else {
            audio.play().catch(() => setError(true));
        }
    };

    const handleTimeUpdate = () => {
        const audio = audioRef.current;
        if (!audio || !duration) return;
        setCurrentTime(audio.currentTime);
        setProgress(audio.currentTime / duration);
    };

    const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
        const audio = audioRef.current;
        if (!audio || !duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        audio.currentTime = pct * duration;
        setProgress(pct);
    };

    if (error) {
        return (
            <div className={styles.audioError}>
                <AlertCircle className={styles.audioErrorIcon} />
                <span>Cannot play audio</span>
            </div>
        );
    }

    return (
        <div className={styles.audioControls}>
            <button
                className={styles.audioPlayBtn}
                onClick={togglePlay}
                disabled={!ready}
            >
                {playing ? (
                    <Pause className={styles.audioPlayIcon} />
                ) : !ready ? (
                    <div className={styles.audioSpinner} />
                ) : (
                    <Play className={styles.audioPlayIcon} />
                )}
            </button>
            <div className={styles.audioTrack} onClick={handleSeek}>
                <div className={styles.audioProgress} style={{ width: `${progress * 100}%` }} />
            </div>
            <span className={styles.audioDuration}>
                {duration > 0 ? formatDuration(playing ? currentTime : duration) : '---'}
            </span>
            <audio
                ref={audioRef}
                src={src}
                preload="auto"
                onCanPlayThrough={() => setReady(true)}
                onLoadedMetadata={(e) => {
                    setDuration(e.currentTarget.duration);
                    setReady(true);
                }}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => { setPlaying(false); setProgress(0); setCurrentTime(0); }}
                onTimeUpdate={handleTimeUpdate}
                onError={() => setError(true)}
            />
        </div>
    );
}

export function GenericMessageBubble({ message }: Props) {
    const isInbound = message.direction === 'INBOUND';
    const isAI = message.senderType === 'AI_AGENT';

    const bubbleClass = `${styles.bubble} ${isInbound ? styles.inbound : styles.outbound} ${isAI ? styles.ai : ''}`;

    const renderContent = () => {
        switch (message.messageType) {
            // ── WhatsApp audio with custom player ────────────────────────────
            case 'AUDIO':
                return (
                    <div className={styles.audioBlock}>
                        {message.mediaUrl ? (
                            <AudioPlayer src={message.mediaUrl} />
                        ) : (
                            <div className={styles.mediaRow}>
                                <Mic className={styles.mediaIcon} />
                                <span className={styles.mediaLabel}>Voice message</span>
                            </div>
                        )}
                        {message.isTranscribed && message.transcription && (
                            <p className={styles.transcription}>{message.transcription}</p>
                        )}
                    </div>
                );

            // ── Image — supports inline preview (Instagram / Messenger) ──────
            case 'IMAGE':
                if (message.mediaUrl) {
                    return (
                        <div className={styles.imageBlock}>
                            <img
                                src={message.mediaUrl}
                                alt="Shared image"
                                className={styles.image}
                                loading="lazy"
                                onError={(e) => {
                                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                                    (e.currentTarget.nextElementSibling as HTMLElement | null)?.removeAttribute('style');
                                }}
                            />
                            <div className={styles.mediaRow} style={{ display: 'none' }}>
                                <Image className={styles.mediaIcon} />
                                <span className={styles.mediaLabel}>Photo</span>
                            </div>
                            {message.content && (
                                <p className={styles.imageCaption}>{message.content}</p>
                            )}
                        </div>
                    );
                }
                return (
                    <div className={styles.mediaRow}>
                        <Image className={styles.mediaIcon} />
                        <span className={styles.mediaLabel}>{message.content || 'Photo'}</span>
                    </div>
                );

            // ── Video (Messenger) ────────────────────────────────────────────
            case 'VIDEO':
                if (message.mediaUrl) {
                    return (
                        <div className={styles.videoBlock}>
                            <video
                                src={message.mediaUrl}
                                controls
                                className={styles.videoMedia}
                                preload="metadata"
                            />
                            {message.content && (
                                <p className={styles.text}>{message.content}</p>
                            )}
                        </div>
                    );
                }
                return (
                    <div className={styles.mediaRow}>
                        <Video className={styles.mediaIcon} />
                        <span className={styles.mediaLabel}>{message.content || 'Video'}</span>
                    </div>
                );

            // ── Document (WhatsApp) ──────────────────────────────────────────
            case 'DOCUMENT':
                return (
                    <div className={styles.mediaRow}>
                        <FileText className={styles.mediaIcon} />
                        <span className={styles.mediaLabel}>{message.content || 'Document'}</span>
                    </div>
                );

            // ── File (Messenger) ─────────────────────────────────────────────
            case 'FILE':
                return (
                    <div className={styles.mediaRow}>
                        <FileText className={styles.mediaIcon} />
                        {message.mediaUrl ? (
                            <a
                                href={message.mediaUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.fileLink}
                            >
                                {message.content || 'Download file'}
                            </a>
                        ) : (
                            <span className={styles.mediaLabel}>{message.content || 'File'}</span>
                        )}
                    </div>
                );

            // ── Location (WhatsApp) ──────────────────────────────────────────
            case 'LOCATION':
                return (
                    <div className={styles.mediaRow}>
                        <MapPin className={styles.mediaIcon} />
                        <span className={styles.mediaLabel}>
                            {message.locationName || message.locationAddress || 'Location'}
                        </span>
                    </div>
                );

            // ── Sticker ──────────────────────────────────────────────────────
            case 'STICKER':
                if (message.mediaUrl) {
                    return (
                        <div className={styles.stickerBlock}>
                            <img
                                src={message.mediaUrl}
                                alt="Sticker"
                                className={styles.stickerImg}
                                onError={(e) => {
                                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                                    (e.currentTarget.nextElementSibling as HTMLElement | null)?.removeAttribute('style');
                                }}
                            />
                            <div className={styles.mediaRow} style={{ display: 'none' }}>
                                <Sticker className={styles.mediaIcon} />
                                <span className={styles.mediaLabel}>Sticker</span>
                            </div>
                        </div>
                    );
                }
                return (
                    <div className={styles.mediaRow}>
                        <Sticker className={styles.mediaIcon} />
                        <span className={styles.mediaLabel}>Sticker</span>
                    </div>
                );

            // ── Instagram: Story Reply ───────────────────────────────────────
            case 'STORY_REPLY':
                return (
                    <div className={styles.storyBlock}>
                        <div className={styles.storyContext}>
                            <BookOpen className={styles.storyContextIcon} />
                            <span className={styles.storyContextLabel}>Replied to your story</span>
                            {message.storyUrl && (
                                <a
                                    href={message.storyUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.storyLink}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <ExternalLink className={styles.storyLinkIcon} />
                                </a>
                            )}
                        </div>
                        {message.mediaUrl && (
                            <img
                                src={message.mediaUrl}
                                alt="Story thumbnail"
                                className={styles.storyThumbnail}
                                onError={(e) => {
                                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                                }}
                            />
                        )}
                        {message.content && <p className={styles.text}>{message.content}</p>}
                    </div>
                );

            // ── Instagram: Story Mention ─────────────────────────────────────
            case 'STORY_MENTION':
                return (
                    <div className={styles.storyBlock}>
                        <div className={styles.storyContext}>
                            <BookOpen className={styles.storyContextIcon} />
                            <span className={styles.storyContextLabel}>Mentioned you in their story</span>
                            {message.storyUrl && (
                                <a
                                    href={message.storyUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.storyLink}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <ExternalLink className={styles.storyLinkIcon} />
                                    <span>View story</span>
                                </a>
                            )}
                        </div>
                        {message.content && <p className={styles.text}>{message.content}</p>}
                    </div>
                );

            // ── Instagram: Media Share ───────────────────────────────────────
            case 'MEDIA_SHARE':
                return (
                    <div className={styles.mediaShareBlock}>
                        <div className={styles.mediaRow}>
                            <Share2 className={styles.mediaIcon} />
                            <span className={styles.mediaLabel}>Shared media</span>
                        </div>
                        {message.mediaUrl && (
                            <img
                                src={message.mediaUrl}
                                alt="Shared media"
                                className={styles.sharedMediaImg}
                                onError={(e) => {
                                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                                }}
                            />
                        )}
                        {message.content && <p className={styles.text}>{message.content}</p>}
                    </div>
                );

            // ── Messenger: Postback ──────────────────────────────────────────
            case 'POSTBACK':
                return (
                    <div className={styles.postbackBlock}>
                        <div className={styles.mediaRow}>
                            <MousePointerClick className={styles.mediaIcon} />
                            <span className={styles.mediaLabel}>{message.content || 'Button click'}</span>
                        </div>
                        {message.postbackPayload && (
                            <span className={styles.postbackPayload}>{message.postbackPayload}</span>
                        )}
                    </div>
                );

            // ── Template ─────────────────────────────────────────────────────
            case 'TEMPLATE':
                return (
                    <div className={styles.templateBlock}>
                        <span className={styles.templateLabel}>Template Message</span>
                        <p className={styles.text}>{message.content}</p>
                    </div>
                );

            default:
                return <p className={styles.text}>{message.content}</p>;
        }
    };

    return (
        <div className={`${styles.row} ${isInbound ? styles.rowLeft : styles.rowRight}`}>
            <div className={bubbleClass}>
                {!isInbound && (
                    <span className={styles.senderTag}>
                        {isAI ? 'AI' : 'Admin'}
                    </span>
                )}
                {renderContent()}
                <span className={styles.time}>{formatTimestamp(message.timestamp)}</span>
            </div>
        </div>
    );
}
